import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Bot,
  Building2,
  Check,
  ChevronRight,
  Clock,
  Globe,
  LayoutDashboard,
  MapPin,
  MessageSquare,
  Shield,
  Smartphone,
  Star,
  TrendingUp,
  Users,
  Zap,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import SiteNavBar from '@/components/SiteNavBar';

// Animation component (matches FlyAgent style)
const RevealOnScroll = ({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTimeout(() => setIsVisible(true), delay * 100); },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${className}`}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(24px)' }}>
      {children}
    </div>
  );
};

// Floating particles background (from FlyAgent)
const ParticleField = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(30)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 rounded-full bg-violet-500/30"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
        50% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
      }
    `}</style>
  </div>
);

const PRODUCTS = [
  {
    id: 'managed',
    name: 'Managed Local SEO',
    tagline: 'Done-for-You by Fly\'s Expert Team',
    audience: 'Solo & Multi-Location Businesses',
    description: 'Fly\'s Local SEO experts manage your Google Business Profile end-to-end using Fly\'s platform. Reviews, rankings, content, profile protection, and reporting — you grant access, we handle everything.',
    icon: Users,
    color: '#25D366',
    gradient: 'from-green-500 to-emerald-400',
    hoverBorder: 'hover:border-green-500/50',
    glowFrom: 'from-green-600/40',
    glowTo: 'to-emerald-600/40',
    textColor: 'text-green-400',
    features: [
      { icon: Star, text: 'Expert review management & responses' },
      { icon: Shield, text: '24/7 profile protection & optimization' },
      { icon: TrendingUp, text: 'Rank tracking & content publishing' },
      { icon: Clock, text: 'Monthly reports — you do nothing' },
    ],
    cta: 'Learn More',
    link: '/managed',
    priceINR: 'From ₹100/day',
    priceUSD: 'From $1.09/day',
  },
  {
    id: 'fly-lite',
    name: 'Fly Lite',
    tagline: 'WhatsApp Assistant for Solo Businesses',
    audience: 'Solo Businesses',
    description: 'Manage your Google Business Profile yourself — entirely through WhatsApp. Review alerts, AI-powered responses, profile protection, and performance reports delivered where you already are. No apps, no dashboards.',
    icon: Smartphone,
    color: '#25D366',
    gradient: 'from-green-500 to-emerald-400',
    hoverBorder: 'hover:border-green-500/50',
    glowFrom: 'from-green-600/40',
    glowTo: 'to-emerald-600/40',
    textColor: 'text-green-400',
    features: [
      { icon: MessageSquare, text: 'AI review responses via WhatsApp' },
      { icon: Shield, text: '24/7 profile protection alerts' },
      { icon: Star, text: 'Review generator with QR codes' },
      { icon: Smartphone, text: 'Everything via WhatsApp — no app needed' },
    ],
    cta: 'Learn More',
    link: '/gmb-whatsapp-assistant',
    priceINR: 'From ₹999/mo',
    priceUSD: 'From $14.99/mo',
  },
  {
    id: 'fly-web-agencies',
    name: 'Fly Web for Agencies',
    tagline: 'White-Label Dashboard for Agencies',
    audience: 'Marketing Agencies & Freelancers',
    description: 'Get your own white-labelled version of Fly\'s Local SEO dashboard. Manage all your clients\' Google Business Profiles under your agency\'s brand — your logo, your domain, your clients.',
    icon: Building2,
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-fuchsia-400',
    hoverBorder: 'hover:border-violet-500/50',
    glowFrom: 'from-violet-600/40',
    glowTo: 'to-fuchsia-600/40',
    textColor: 'text-violet-400',
    features: [
      { icon: Users, text: 'White-label branding & custom domain' },
      { icon: Star, text: 'Full platform — reviews, rankings, content' },
      { icon: Globe, text: 'Multi-client workspace management' },
      { icon: Zap, text: 'Branded reports & analytics' },
    ],
    cta: 'Learn More',
    link: '/agencies-fly-web',
    priceINR: '₹999/location/mo',
    priceUSD: '$14.99/location/mo',
  },
  {
    id: 'fly-agent-agencies',
    name: 'Fly Agent for Agencies',
    tagline: 'AI Agents for Agency Workflows — Coming Soon',
    audience: 'Marketing Agencies & Freelancers',
    description: 'Connect your favorite AI assistant — Claude, ChatGPT, or Gemini — to your clients\' Google Business Profiles. 14 specialized agents handle reviews, rankings, content, reporting and more through natural conversation.',
    icon: Bot,
    color: '#d946ef',
    gradient: 'from-fuchsia-500 to-cyan-400',
    hoverBorder: 'hover:border-fuchsia-500/50',
    glowFrom: 'from-fuchsia-600/40',
    glowTo: 'to-cyan-600/40',
    textColor: 'text-fuchsia-400',
    features: [
      { icon: Bot, text: '14 specialized AI agents' },
      { icon: Users, text: 'Manage 50+ client profiles' },
      { icon: Globe, text: 'Works with Claude, ChatGPT & Gemini' },
      { icon: Sparkles, text: 'Coming soon — get early access' },
    ],
    cta: 'Get Early Access',
    link: '/agencies-fly-agent',
    priceINR: 'Coming Soon',
    priceUSD: 'Coming Soon',
  },
  {
    id: 'fly-diy',
    name: 'Fly DIY',
    tagline: 'DIY Dashboard',
    audience: 'Solo & Multi-Location Brands',
    description: 'A full-featured dashboard to manage your Google Business Profile yourself. Track rankings, respond to reviews, create content, monitor competitors, and generate reports — all from one place.',
    icon: LayoutDashboard,
    color: '#06b6d4',
    gradient: 'from-cyan-500 to-blue-400',
    hoverBorder: 'hover:border-cyan-500/50',
    glowFrom: 'from-cyan-600/40',
    glowTo: 'to-blue-600/40',
    textColor: 'text-cyan-400',
    features: [
      { icon: LayoutDashboard, text: 'Full-featured management dashboard' },
      { icon: TrendingUp, text: 'Rankings, analytics & insights' },
      { icon: Building2, text: 'Multi-location support' },
      { icon: MapPin, text: 'Local SEO tools & reports' },
    ],
    cta: 'Learn More',
    link: '/fly-diy',
    priceINR: '₹999/location/mo',
    priceUSD: '$14.99/location/mo',
  },
];


declare global {
  interface Window {
    __GEO_COUNTRY__?: string;
  }
}

const FlyHomePage = () => {
  const [isIndianUser, setIsIndianUser] = useState(true);

  useEffect(() => {
    const VERCEL_EDGE_REGIONS = ['SG', 'HK', 'JP', 'KR', 'AU', 'NZ'];

    const detectCountry = async () => {
      const geoCountry = typeof window !== 'undefined' ? window.__GEO_COUNTRY__ : undefined;

      if (geoCountry && VERCEL_EDGE_REGIONS.includes(geoCountry)) {
        try {
          const response = await fetch('/api/geo');
          const data = await response.json();
          if (data.countryCode && data.countryCode !== 'IN') setIsIndianUser(false);
          return;
        } catch { return; }
      }

      if (geoCountry && geoCountry !== 'IN') setIsIndianUser(false);
    };

    detectCountry();
  }, []);

  return (
    <>
      <Helmet>
        <title>Fly | Local SEO Platform for Businesses & Agencies</title>
        <meta name="description" content="Manage your Google Business Profile with Fly. Choose managed services via WhatsApp, AI-powered tools for agencies, or a full DIY dashboard. Track rankings, respond to reviews, protect your profile." />
        <meta property="og:title" content="Fly | Local SEO Platform for Businesses & Agencies" />
        <meta property="og:description" content="Google Business Profile management — managed for you, powered by AI, or self-service. Choose your path." />
        <link rel="canonical" href="https://fly-social.com/" />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-white">
        <SiteNavBar />

        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-16">
          <ParticleField />

          {/* Grid background */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '64px 64px',
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-b from-violet-950/20 via-transparent to-gray-950" />

          <div className="relative z-10 container mx-auto px-4 text-center">
            <RevealOnScroll>
              {/* Logo */}
              <div className="flex items-center justify-center mb-8">
                <img
                  src="/lovable-uploads/2e7bf5aa-9c7b-41ea-b64a-48b87f3dc01d.png"
                  alt="Fly Logo"
                  className="h-16 md:h-20 brightness-0 invert"
                />
              </div>

              <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400">
                  Local SEO,
                </span>
                <br />
                <span className="text-white">Your Way</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-4 leading-relaxed">
                One platform, multiple ways to manage your Google Business Profile.
              </p>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-12">
                Whether you want it <span className="text-green-400">managed by our experts</span>,
                handled <span className="text-green-400">via WhatsApp</span>,
                powered by <span className="text-violet-400">AI agents</span>,
                or <span className="text-cyan-400">Fly DIY dashboard</span> — Fly has you covered.
              </p>

              {/* Quick nav pills */}
              <div className="flex flex-wrap gap-3 justify-center">
                {PRODUCTS.map((product) => (
                  <a key={product.id} href={`#${product.id}`}>
                    <Button
                      variant="outline"
                      className={`bg-transparent border-gray-700 hover:bg-gray-800 text-gray-300 hover:text-white rounded-full px-6 py-5 text-sm`}
                    >
                      <product.icon className="w-4 h-4 mr-2" style={{ color: product.color }} />
                      {product.name}
                    </Button>
                  </a>
                ))}
              </div>
            </RevealOnScroll>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronRight className="w-6 h-6 text-gray-600 rotate-90" />
          </div>
        </section>

        {/* Products Section */}
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-violet-950/10 to-gray-950" />

          <div className="relative container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
                    Choose Your Path
                  </span>
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Every business is different. Pick the approach that fits how you work.
                </p>
              </div>
            </RevealOnScroll>

            <div className="flex flex-col gap-6 max-w-5xl mx-auto">
              {PRODUCTS.map((product, index) => {
                const Icon = product.icon;
                return (
                  <RevealOnScroll key={product.id} delay={index}>
                    <div id={product.id} className="scroll-mt-24">
                      <Link to={product.link} className="block group">
                        <div className="relative">
                          {/* Glow effect */}
                          <div className={`absolute -inset-0.5 bg-gradient-to-r ${product.glowFrom} ${product.glowTo} rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                          {/* Card — horizontal row */}
                          <div className={`relative bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 ${product.hoverBorder} transition-all duration-300 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6`}>
                            {/* Icon */}
                            <div
                              className={`w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br ${product.gradient} p-3 md:p-3.5 shadow-lg flex-shrink-0`}
                              style={{ boxShadow: `0 8px 32px ${product.color}40` }}
                            >
                              <Icon className="w-full h-full text-white" />
                            </div>

                            {/* Content */}
                            <div className="flex-grow min-w-0">
                              <div className="flex flex-wrap items-center gap-3 mb-1">
                                <h3 className="text-2xl font-bold text-white">{product.name}</h3>
                                <span className="px-2.5 py-0.5 rounded-full bg-gray-800 border border-gray-700 text-xs text-gray-400">
                                  For {product.audience}
                                </span>
                              </div>
                              <p className={`text-sm font-medium ${product.textColor} mb-2`}>{product.tagline}</p>
                              <p className="text-gray-400 text-sm leading-relaxed">{product.description}</p>

                              {/* Features — inline on desktop */}
                              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3">
                                {product.features.map((feat, i) => (
                                  <span key={i} className="flex items-center gap-2 text-xs text-gray-400">
                                    <feat.icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: product.color }} />
                                    {feat.text}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Price + CTA */}
                            <div className="flex md:flex-col items-center md:items-end gap-3 md:gap-1 flex-shrink-0 md:pl-6 md:border-l md:border-gray-800">
                              <span className="text-sm text-gray-500">{isIndianUser ? product.priceINR : product.priceUSD}</span>
                              <div className={`flex items-center ${product.textColor} text-sm font-medium group-hover:translate-x-2 transition-transform whitespace-nowrap`}>
                                {product.cta} <ArrowRight className="w-4 h-4 ml-2" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </RevealOnScroll>
                );
              })}
            </div>
          </div>
        </section>


        {/* Who It's For */}
        <section className="py-24 relative">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Built for Every Business</h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  From a single storefront to a nationwide franchise, Fly scales with you
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {[
                {
                  title: 'Busy Business Owners',
                  description: 'No time to manage Google yourself? Our Local SEO experts handle everything — reviews, rankings, content, and reports.',
                  recommendation: 'Managed Local SEO',
                  recColor: '#25D366',
                  link: '/managed',
                  icon: Star,
                },
                {
                  title: 'Solo Businesses',
                  description: 'Want to stay in control but keep it simple? Manage your GBP entirely via WhatsApp — review alerts, AI responses, and protection.',
                  recommendation: 'Fly Lite',
                  recColor: '#25D366',
                  link: '/gmb-whatsapp-assistant',
                  icon: Smartphone,
                },
                {
                  title: 'Agencies & Freelancers',
                  description: 'Managing dozens of client profiles? Get your own white-label dashboard with your brand, domain, and reports.',
                  recommendation: 'Fly Web for Agencies',
                  recColor: '#8b5cf6',
                  link: '/agencies-fly-web',
                  icon: Users,
                },
                {
                  title: 'Multi-Location Brands',
                  description: 'Franchise, chain, or healthcare network? Get a centralized dashboard with per-location analytics and reporting.',
                  recommendation: 'Fly DIY',
                  recColor: '#06b6d4',
                  link: '/fly-diy',
                  icon: Building2,
                },
              ].map((item, index) => (
                <RevealOnScroll key={index} delay={index}>
                  <Link to={item.link} className="block h-full group">
                    <div className="relative h-full">
                      <div className="absolute -inset-0.5 rounded-2xl blur opacity-0 group-hover:opacity-40 transition-opacity duration-500"
                        style={{ background: `linear-gradient(135deg, ${item.recColor}40, transparent)` }} />
                      <div className="relative h-full bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-gray-700 transition-all duration-300 p-8">
                        <div className="w-12 h-12 rounded-xl p-2.5 mb-6" style={{ backgroundColor: `${item.recColor}20` }}>
                          <item.icon className="w-full h-full" style={{ color: item.recColor }} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                        <p className="text-gray-400 text-sm mb-6 leading-relaxed">{item.description}</p>
                        <div className="flex items-center gap-2 mt-auto">
                          <span className="text-xs text-gray-500">Recommended:</span>
                          <span className="text-xs font-semibold" style={{ color: item.recColor }}>{item.recommendation}</span>
                          <ArrowRight className="w-3 h-3 ml-auto" style={{ color: item.recColor }} />
                        </div>
                      </div>
                    </div>
                  </Link>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-950/50 via-fuchsia-950/50 to-cyan-950/50" />
          <ParticleField />

          <div className="relative container mx-auto px-4 text-center">
            <RevealOnScroll>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Ready to Dominate Local Search?
              </h2>
              <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
                Pick your path and get started in minutes. No contracts, cancel anytime.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
                <Link to="/managed">
                  <Button size="lg" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-green-500/25">
                    <Users className="w-5 h-5 mr-2" />
                    Managed Services
                  </Button>
                </Link>
                <Link to="/gmb-whatsapp-assistant">
                  <Button size="lg" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-green-500/25">
                    <Smartphone className="w-5 h-5 mr-2" />
                    Fly Lite (WhatsApp)
                  </Button>
                </Link>
                <Link to="/agencies-fly-web">
                  <Button size="lg" className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25">
                    <Building2 className="w-5 h-5 mr-2" />
                    For Agencies
                  </Button>
                </Link>
                <Link to="/fly-diy">
                  <Button size="lg" className="bg-transparent border border-gray-600 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl">
                    <LayoutDashboard className="w-5 h-5 mr-2" />
                    Fly DIY
                  </Button>
                </Link>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        <Footer variant="dark" />
      </div>
    </>
  );
};

export default FlyHomePage;
